import type { RequestBodyObject } from 'oas/types';
import type { OpenAPIV3 } from 'openapi-types';

import React, { useMemo } from 'react';

import useClassy from '@core/hooks/useClassy';
import { useAPIDesignerStore } from '@core/store';

import APISectionHeader from '@ui/API/SectionHeader';
import TypeMenu, { validatedSchemaType, TypeMenuButton, type SchemaFormat } from '@ui/APIDesigner/TypeMenu';
import Button from '@ui/Button';
import Dropdown from '@ui/Dropdown';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Menu, { MenuItem } from '@ui/Menu';

import useApiDesignerValidation from '../hooks/useApiDesignerValidation';
import apiDesignerClasses from '../style.module.scss';

import classes from './style.module.scss';

interface SchemaProps {
  /**
   * For a schema that is not the top level schema, this is the list of all other parameters
   * that are siblings to this schema. This is used to validate the name of the schema
   * to make sure it is unique.
   */
  allOtherParameters: string[];

  /**
   * Whether this schema is required in the parent schema.
   * This is used to determine whether the required checkbox should be checked or not.
   * This is only used for schemas that are not the top level schema.
   */
  isRequired?: boolean;

  /**
   * The name of the schema. This is used to validate the name of the schema
   * to make sure it is unique within the parent schema.
   */
  name?: string;

  /**
   * The schema for the current request body parameter.
   */
  schema: OpenAPIV3.SchemaObject;

  /**
   * The schemaPropertyMapping is used to determine the level of the schema in the tree.
   * The indexes are separated by a dash and are 0-indexed.
   *
   * For example, if the schemaPropertyMapping is `0`, then this is the top level schema.
   * If the schemaPropertyMapping is `0-1`, then this is a second property of the top level schema.
   * If the schemaPropertyMapping is `0-1-5`, then this is a sixth property of the second property of the top level schema.
   */
  schemaPropertyMapping: string;
}

const Schema = ({ allOtherParameters, isRequired, name, schema, schemaPropertyMapping }: SchemaProps) => {
  const bem = useClassy(apiDesignerClasses, 'Parameter');
  const [add, remove, updateSchemaAttribute] = useAPIDesignerStore(s => [
    s.requestBodyEditor.add,
    s.requestBodyEditor.remove,
    s.requestBodyEditor.updateSchemaAttribute,
  ]);
  const isTopLevel = schemaPropertyMapping === '0';

  const { error: nameError, field: nameFieldProps } = useApiDesignerValidation<string>({
    name: `parameter-${schemaPropertyMapping}-name`,
    validate: {
      beforeChange: (value: string) => {
        const isUnique = !allOtherParameters?.includes(value);
        return isUnique || `There is already a parameter with the name '${value}'. Please choose a different name.`;
      },
      required: 'Parameter name is required',
    },
    value: name || '',
    handleChange: val => updateSchemaAttribute(schemaPropertyMapping, { attribute: 'name', value: val }),
  });

  let isNestableSchema = false;
  let nestedSchemas: (JSX.Element | null)[] = [null];

  if (schema.type === 'object') {
    isNestableSchema = true;
    nestedSchemas = Object.keys(schema.properties || {}).map((propertyName, idx, allParameterNames) => {
      const allOtherParametersCurrent = allParameterNames.filter((_, i) => i !== idx);
      const property = schema.properties![propertyName];

      if (!property) return null;

      return (
        <Schema
          key={idx}
          allOtherParameters={allOtherParametersCurrent}
          isRequired={schema.required?.includes(propertyName)}
          name={propertyName}
          schema={property as OpenAPIV3.SchemaObject}
          schemaPropertyMapping={`${schemaPropertyMapping}-${idx}`}
        />
      );
    });
  } else if (schema.type === 'array' && schema.items && 'type' in schema.items && schema.items.type === 'object') {
    isNestableSchema = true;
    nestedSchemas = Object.keys(schema.items.properties || {}).map((propertyName, idx, allParameterNames) => {
      const allOtherParametersCurrent = allParameterNames.filter((_, i) => i !== idx);
      const property = 'properties' in schema.items && schema.items.properties?.[propertyName];

      if (!property || '$ref' in schema.items) return null;

      return (
        <Schema
          key={idx}
          allOtherParameters={allOtherParametersCurrent}
          isRequired={schema.items.required?.includes(propertyName)}
          name={propertyName}
          schema={property as OpenAPIV3.SchemaObject}
          schemaPropertyMapping={`${schemaPropertyMapping}-${idx}`}
        />
      );
    });
  }

  return (
    <Flex align="stretch" className={classes.RequestBodyEditor} gap="0" justify="start" layout="col">
      <Flex align="stretch" gap="0" layout="col">
        <div className={classes['RequestBodyEditor-group']}>
          <Flex align="baseline" gap="2px" justify="start">
            {!isTopLevel && (
              <div className={bem('-inputSizer')}>
                <input
                  className={bem('-input', '-input_name', nameError && '-input_error')}
                  data-1p-ignore
                  placeholder="Name"
                  spellCheck="false"
                  {...nameFieldProps}
                />
                <span aria-hidden="true" className={apiDesignerClasses['Parameter-inputSizer-clone']}>
                  {nameFieldProps.value}
                </span>
              </div>
            )}

            <Dropdown justify="start">
              <TypeMenuButton className={bem('-input', '-input_type')} schema={schema}>
                {validatedSchemaType(schema).label}
                {!isTopLevel && <Icon name="chevron-down" />}
              </TypeMenuButton>
              {!isTopLevel && (
                <TypeMenu
                  format={schema.format as SchemaFormat}
                  setNewType={newType =>
                    updateSchemaAttribute(schemaPropertyMapping, { attribute: 'type', value: newType })
                  }
                  shouldShowAllowObject={true}
                  type={schema.type || 'string'}
                />
              )}
            </Dropdown>

            {!isTopLevel && (
              <button
                aria-checked={isRequired}
                className={bem('-input', '-input_required', isRequired && '-input_required_checked')}
                onClick={() => {
                  updateSchemaAttribute(schemaPropertyMapping, { attribute: 'required', value: !isRequired });
                }}
                role="switch"
                type="button"
              >
                <span>required</span>
              </button>
            )}
          </Flex>
          <Flex align="center" gap="2px" justify="end">
            {!isNestableSchema && (
              <input
                className={bem('-input', '-input_form')}
                name="default"
                onChange={e =>
                  updateSchemaAttribute(schemaPropertyMapping, { attribute: 'default', value: e.currentTarget.value })
                }
                placeholder="Default Value"
                value={schema.default || ''}
              />
            )}
            <Dropdown clickInToClose>
              <Button ghost kind="minimum" size="xs">
                <Icon name="more-vertical" />
              </Button>
              <Menu>
                <MenuItem color="red" icon="icon icon-trash1" onClick={() => remove(schemaPropertyMapping)}>
                  Delete
                </MenuItem>
              </Menu>
            </Dropdown>
            {isNestableSchema ? (
              <Button kind="secondary" onClick={() => add(schemaPropertyMapping)} outline size="xs">
                <Icon name="plus" />
              </Button>
            ) : null}
          </Flex>
        </div>
        {!isTopLevel && (
          <input
            className={bem('-description')}
            onChange={e =>
              updateSchemaAttribute(schemaPropertyMapping, { attribute: 'description', value: e.currentTarget.value })
            }
            placeholder="Description"
            value={schema.description || ''}
          />
        )}
        {!!nameError && <span className={bem('-error')}>{nameError}</span>}
      </Flex>
      {nestedSchemas}
    </Flex>
  );
};

const RequestBodyEditor = () => {
  const [getCurrentOperation, init, method] = useAPIDesignerStore(s => [
    s.getCurrentOperation,
    s.requestBodyEditor.init,
    s.apiObject?.method,
  ]);
  const { operation } = getCurrentOperation();

  // TODO: should check if it's a ref here
  // https://linear.app/readme-io/issue/RM-8850/object-definitionsrefs

  // TODO: this really only works if the request body is an object
  // and doesn't really handle other types of request bodies
  // https://linear.app/readme-io/issue/RM-9361/initially-creating-request-bodies
  const requestBody = operation?.requestBody as RequestBodyObject;

  const schema = useMemo(
    () =>
      requestBody?.content?.['application/json']?.schema
        ? requestBody.content['application/json'].schema
        : { properties: {}, type: 'object' },
    [requestBody?.content],
  ) as OpenAPIV3.SchemaObject;

  // While we don't recommend request bodies for non-write methods,
  // and don't allow users to add new request bodies for them,
  // we should still render the editor for existing request bodies.
  const isValidMethod = useMemo(() => {
    return method && ['post', 'put', 'patch'].includes(method);
  }, [method]);

  if (operation?.requestBody) {
    return (
      <section className={classes['RequestBodyEditor-section']}>
        <APISectionHeader heading={'Request Body'} />
        <Schema allOtherParameters={Object.keys(schema.properties || {})} schema={schema} schemaPropertyMapping="0" />
      </section>
    );
  }

  // Render a way to add an empty request body
  // TODO: Eventually this should support additional request body types https://linear.app/readme-io/issue/RM-9573/support-for-request-content-types-other-than-applicationjson
  if (isValidMethod) {
    return (
      <section>
        <APISectionHeader heading={'Request Body'}>
          <Button kind="secondary" onClick={init} outline size="xs">
            <Icon name="plus" />
          </Button>
        </APISectionHeader>
      </section>
    );
  }

  return null;
};

export default RequestBodyEditor;
